import type { DockPanel } from "../../../../stores/dock";

/**
 * The browser kind's payload (bots B2, 2026-09-03): ONE agent screen — a
 * real Chromium context the agent is reading — watched live in the dock,
 * taken over by the user when they want to drive it themselves, handed back
 * with Resume. `screenId` may be empty: the panel then offers the picker
 * (open screens + "new screen" + a URL to open).
 */
export interface BrowserData {
	screenId: string;
}

export type ScreenStatus = "idle" | "working" | "taken_over" | "handoff" | "closed";

/** A step the page asked the USER for (bots B3): the agent is paused until
 *  Done / Cancel / the timeout. Carried on the screen row by the socket. */
export interface HandoffInfo {
	id: string;
	kind: string;
	host: string;
	url: string;
	status: string;
	message: string;
	reason?: string;
}

export interface ScreenRow {
	id: string;
	agent_key: string;
	status: ScreenStatus | string;
	url: string;
	title: string;
	idle_seconds?: number;
	blocked_requests?: number;
	viewers?: number;
	held_submits?: number;
	handoff?: HandoffInfo | null;
}

/** Read a browser panel's payload. Total by construction — `data` is
 *  `unknown` on DockPanel, so exactly one place narrows it. */
export function browserData(panel: DockPanel): BrowserData {
	const d = panel.data as Partial<BrowserData> | undefined;
	return { screenId: d?.screenId ?? "" };
}

/** ONE browser panel per screen (stable id → re-opening focuses the tab). */
export function browserPanel(screenId = "", title = ""): DockPanel {
	return {
		id: screenId ? `browser:${screenId}` : "browser:picker",
		kind: "browser",
		title: screenId ? `Screen · ${title || screenId}` : "Browser",
		data: { screenId } satisfies BrowserData,
		// Global: an agent's screen is reachable from any module, like the terminal.
	};
}

/** A frame's `data:` URL from the base64 JPEG the socket sends. */
export function frameSrc(b64: string): string {
	return `data:image/jpeg;base64,${b64}`;
}

/** Map a pointer position on the DISPLAYED frame back to the screen's own
 *  viewport pixels. The image is letterboxed inside its box, so the offset
 *  and the scale come from the natural size, never from the box alone. */
export function toViewportPoint(
	client: { x: number; y: number },
	rect: { left: number; top: number; width: number; height: number },
	natural: { width: number; height: number },
): { x: number; y: number } | null {
	if (!natural.width || !natural.height || !rect.width || !rect.height) return null;
	const scale = Math.min(rect.width / natural.width, rect.height / natural.height);
	const drawnW = natural.width * scale;
	const drawnH = natural.height * scale;
	const offX = rect.left + (rect.width - drawnW) / 2;
	const offY = rect.top + (rect.height - drawnH) / 2;
	const x = (client.x - offX) / scale;
	const y = (client.y - offY) / scale;
	if (x < 0 || y < 0 || x > natural.width || y > natural.height) return null;
	return { x: Math.round(x), y: Math.round(y) };
}

/** The status chip's words — one place, so the panel and the Settings list
 *  cannot describe the same state two ways. */
export function statusLabel(status: string, t: (k: string, d: string) => string): string {
	switch (status) {
		case "working":
			return t("dock.browser.statusWorking", "Working");
		case "taken_over":
			return t("dock.browser.statusTakenOver", "You have the screen");
		case "handoff":
			return t("dock.browser.statusHandoff", "Needs you");
		case "closed":
			return t("dock.browser.statusClosed", "Closed");
		default:
			return t("dock.browser.statusIdle", "Idle");
	}
}

/** The handoff kind's words — one place for the banner and the Settings list. */
export function handoffLabel(kind: string, t: (k: string, d: string) => string): string {
	switch (kind) {
		case "login":
			return t("dock.browser.handoffLogin", "Sign in");
		case "captcha":
			return t("dock.browser.handoffCaptcha", "CAPTCHA");
		case "two_factor":
			return t("dock.browser.handoffTwoFactor", "One-time code");
		case "payment":
			return t("dock.browser.handoffPayment", "Payment details");
		case "webauthn":
			return t("dock.browser.handoffWebauthn", "Passkey / security key");
		default:
			return kind;
	}
}

/** Keys the take-over forwards as a `press` (everything else is typed text). */
export const SPECIAL_KEYS = new Set([
	"Enter", "Tab", "Backspace", "Delete", "Escape", "ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight",
	"Home", "End", "PageUp", "PageDown", "F5",
]);
